import React, { useEffect, useState } from "react";

import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  makeStyles,
  TextField,
  Typography,
  Backdrop,
  CircularProgress,
} from "@material-ui/core";

import Autocomplete from "@material-ui/lab/Autocomplete";

import VisibilityIcon from "@material-ui/icons/Visibility";
import LocalOfferIcon from "@material-ui/icons/LocalOffer";
import EventIcon from "@material-ui/icons/Event";
import FormatListNumberedIcon from "@material-ui/icons/FormatListNumbered";

import {
  AuctionListLimit,
  printLot,
} from "../../../adminModel/auctions";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },

  card: {
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    boxShadow: "none",
    overflow: "hidden",
    backgroundColor: "#fff",
  },

  sectionHeader: {
    padding: theme.spacing(2.25, 2.5),
    backgroundColor: "#fafafa",
  },

  titleRow: {
    display: "flex",
    alignItems: "center",
  },

  headerIcon: {
    width: 40,
    height: 40,
    minWidth: 40,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#eef2ff",
    color: theme.palette.primary.main,
    marginRight: 12,
  },

  title: {
    fontSize: 16,
    fontWeight: 600,
    color: "#1f2937",
  },

  subtitle: {
    marginTop: 3,
    fontSize: 12,
    color: "#6b7280",
  },

  content: {
    padding: theme.spacing(2.5),
  },

  field: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
    },
  },

  textArea: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
    },
  },

  infoBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderRadius: 8,
    border: "1px solid #e5e7eb",
    backgroundColor: "#f8fafc",
  },

  infoRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 7,

    "&:last-child": {
      marginBottom: 0,
    },
  },

  infoLabel: {
    fontSize: 12,
    color: "#6b7280",
  },

  infoValue: {
    fontSize: 12,
    fontWeight: 600,
    color: "#111827",
  },

  actionSection: {
    display: "flex",
    justifyContent: "flex-end",
    padding: theme.spacing(2),
  },

  actionButton: {
    minHeight: 42,
    borderRadius: 8,
    textTransform: "none",
    fontWeight: 600,
    paddingLeft: 18,
    paddingRight: 18,
    boxShadow: "none",
  },

  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
}));

// =====================================================
// HTML ESCAPE
// =====================================================

const escapeHtml = (value) => {
  if (
    value === null ||
    value === undefined
  ) {
    return "";
  }

  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
};

// =====================================================
// HTML CONTENT
// =====================================================

const normalizeHtml = (value) => {
  if (!value) return "";

  return String(value)
    .replace(/\n/g, "<br />")
    .trim();
};

// =====================================================
// COMPONENT
// =====================================================

const Views = () => {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);

  const [geteventList, setEventList] = useState([]);

  const [lotNumberData, setLotNumber] =
    useState("");

  const [eventId, setEventId] =
    useState("");

  const [eventName, setEventName] =
    useState("");

  const [data1, setData1] =
    useState([]);

  // ===================================================
  // GET AUCTIONS
  // ===================================================

  useEffect(() => {
    const fetchAuctions = async () => {
      try {
        setLoading(true);

        const results =
          await AuctionListLimit();

        setEventList(
          results && results.data
            ? results.data
            : []
        );
      } catch (error) {
        console.error(
          "Auction loading error:",
          error
        );
      } finally {
        setLoading(false);
      }
    };

    fetchAuctions();
  }, []);

  // ===================================================
  // FETCH LOT DATA
  // ===================================================

  const fetchDataFromServer = async () => {
    if (!eventId) {
      alert(
        "Please select an Event / Auction."
      );
      return null;
    }

    if (!lotNumberData.trim()) {
      alert(
        "Please enter at least one Lot Number."
      );
      return null;
    }

    try {
      setLoading(true);

      const lotArray =
        lotNumberData
          .split(/[\n,\s]+/)
          .map((lot) => lot.trim())
          .filter(Boolean);

      const formattedLots =
        lotArray.join(",");

      const results = await printLot(
        formattedLots,
        eventId
      );

      if (
        !results ||
        !Array.isArray(results) ||
        results.length === 0
      ) {
        setData1([]);

        alert(
          "No lot records found."
        );

        return null;
      }

      const sorted = [...results].sort(
        (a, b) =>
          Number(a.lot_number) -
          Number(b.lot_number)
      );

      setData1(sorted);

      return sorted;
    } catch (error) {
      console.error(
        "Lot fetching error:",
        error
      );

      setData1([]);

      alert(
        "Failed to fetch lot records."
      );

      return null;
    } finally {
      setLoading(false);
    }
  };

  // ===================================================
  // PREVIEW
  // ===================================================

  const handlePreview = async () => {
    const freshData =
      await fetchDataFromServer();

    if (
      !freshData ||
      freshData.length === 0
    ) {
      return;
    }

    openPrintPreview(freshData);
  };

  // ===================================================
  // CREATE LOT HTML
  // ===================================================

  const renderLotHtml = (
    lot,
    showDivider
  ) => {
    const artist =
      normalizeHtml(lot.artist);

    const title =
      normalizeHtml(lot.title);

    const signed =
      normalizeHtml(lot.signed_dated);

    const medium =
      normalizeHtml(lot.MEDIUM);

    const size =
      normalizeHtml(lot.size);

    const image = lot.img_url
      ? `
          <img
            src="${escapeHtml(
              lot.img_url
            )}"
            class="lot-image"
            alt="Lot"
          />
        `
      : `
          <div class="no-image">
            No Image
          </div>
        `;

    return `
      <div class="sticker-item">

        <div class="sticker-content">

          <!-- IMAGE -->

          <div class="image-wrapper">
            ${image}
          </div>

          <!-- DETAILS -->

          <div class="lot-details">

            <div
              class="event-name editable"
              contenteditable="true"
            >
              ${escapeHtml(
                eventName
              )}
            </div>

            <div
              class="lot-number editable"
              contenteditable="true"
            >
              LOT NO. ${escapeHtml(
                lot.lot_number
              )}
            </div>

            ${
              artist
                ? `
                  <div class="detail-block">

                    <div class="field-label">
                      ARTIST
                    </div>

                    <div
                      class="artist editable"
                      contenteditable="true"
                    >
                      ${artist}
                    </div>

                  </div>
                `
                : ""
            }

            ${
              title
                ? `
                  <div class="detail-block">

                    <div class="field-label">
                      TITLE
                    </div>

                    <div
                      class="art-title editable"
                      contenteditable="true"
                    >
                      ${title}
                    </div>

                  </div>
                `
                : ""
            }

            <div class="extra-details">

              ${
                signed
                  ? `
                    <div
                      class="extra-line editable"
                      contenteditable="true"
                    >
                      ${signed}
                    </div>
                  `
                  : ""
              }

              ${
                medium
                  ? `
                    <div
                      class="extra-line editable"
                      contenteditable="true"
                    >
                      ${medium}
                    </div>
                  `
                  : ""
              }

              ${
                size
                  ? `
                    <div
                      class="extra-line editable"
                      contenteditable="true"
                    >
                      ${size}
                    </div>
                  `
                  : ""
              }

            </div>

          </div>

        </div>

        ${
          showDivider
            ? `
              <div class="cut-line"></div>
            `
            : ""
        }

      </div>
    `;
  };

  // ===================================================
  // PRINT PREVIEW WINDOW
  // ===================================================

  const openPrintPreview = (
    lots
  ) => {
    /*
      IMPORTANT:
      3 LOTS MAXIMUM PER A4 PAGE
    */

    const ITEMS_PER_PAGE = 3;

    const pages = [];

    for (
      let i = 0;
      i < lots.length;
      i += ITEMS_PER_PAGE
    ) {
      pages.push(
        lots.slice(
          i,
          i + ITEMS_PER_PAGE
        )
      );
    }

    const pagesHtml = pages
      .map(
        (pageLots, pageIndex) => {
          const lotHtml =
            pageLots
              .map(
                (lot, index) =>
                  renderLotHtml(
                    lot,
                    index <
                      pageLots.length -
                        1
                  )
              )
              .join("");

          return `
            <div class="paper">

              ${lotHtml}

            </div>
          `;
        }
      )
      .join("");

    const previewWindow =
      window.open(
        "",
        "_blank",
        "width=1100,height=900"
      );

    if (!previewWindow) {
      alert(
        "Please allow pop-ups for this website."
      );
      return;
    }

    // =================================================
    // DOCUMENT
    // =================================================

    previewWindow.document.open();

    previewWindow.document.write(`
      <!DOCTYPE html>

      <html>

        <head>

          <meta charset="UTF-8" />

          <title>
            Sticker Generation
          </title>

          <style>

            /* =========================================
               PAGE
            ========================================= */

            @page {
              size: A4 portrait;
              margin: 0;
            }

            * {
              box-sizing: border-box;
            }

            html,
            body {
              margin: 0;
              padding: 0;

              background: #e5e7eb;

              color: #000;
            }

            body {
              font-family:
                Arial,
                Helvetica,
                sans-serif;

              padding-top: 60px;
              padding-bottom: 30px;
            }

            /* =========================================
               TOP BAR
            ========================================= */

            .topbar {
              position: fixed;

              top: 0;
              left: 0;
              right: 0;

              height: 60px;

              display: flex;

              align-items: center;

              justify-content: flex-end;

              padding:
                0 20px;

              background:
                #ffffff;

              border-bottom:
                1px solid #d1d5db;

              box-shadow:
                0 2px 8px
                rgba(
                  0,
                  0,
                  0,
                  0.08
                );

              z-index: 9999;
            }

            .print-button {
              border: none;

              background:
                #1976d2;

              color: #ffffff;

              padding:
                10px 18px;

              border-radius: 7px;

              font-family:
                Arial,
                sans-serif;

              font-size: 14px;

              font-weight: 600;

              cursor: pointer;
            }

            .print-button:hover {
              background:
                #1565c0;
            }

            /* =========================================
               A4 PAPER
            ========================================= */

            .paper {
              width: 210mm;

              height: 297mm;

              margin:
                20px auto;

              padding:
                9mm 10mm;

              background:
                #ffffff;

              box-shadow:
                0 5px 25px
                rgba(
                  0,
                  0,
                  0,
                  0.15
                );

              overflow: hidden;
            }

            /* =========================================
               EACH STICKER
            ========================================= */

            .sticker-item {
              height:
                91mm;

              width: 100%;

              position: relative;

              page-break-inside:
                avoid;

              break-inside:
                avoid;
            }

            .sticker-content {
              height:
                85mm;

              width: 100%;

              display: flex;

              align-items: center;

              padding:
                4mm 4mm;

              overflow: hidden;
            }

            /* =========================================
               IMAGE
            ========================================= */

            .image-wrapper {
              width:
                70mm;

              height:
                75mm;

              display: flex;

              align-items: center;

              justify-content: center;

              flex-shrink: 0;

              padding-right:
                5mm;
            }

            .lot-image {
              max-width:
                100%;

              max-height:
                70mm;

              width: auto;

              height: auto;

              object-fit:
                contain;

              display: block;
            }

            .no-image {
              width:
                60mm;

              height:
                60mm;

              display: flex;

              align-items: center;

              justify-content: center;

              border:
                1px dashed #aaa;

              font-size:
                10px;

              color:
                #777;
            }

            /* =========================================
               DETAILS
            ========================================= */

            .lot-details {
              flex: 1;

              height:
                75mm;

              display: flex;

              flex-direction: column;

              justify-content:
                center;

              font-family:
                Arial,
                Helvetica,
                sans-serif;

              overflow: hidden;
            }

            .event-name {
              font-size:
                11px;

              font-weight:
                700;

              color:
                #666;

              text-transform:
                uppercase;

              letter-spacing:
                1px;

              margin-bottom:
                2mm;

              line-height:
                1.2;
            }

            .lot-number {
              font-size:
                23px;

              font-weight:
                700;

              color:
                #000;

              margin-bottom:
                4mm;

              line-height:
                1.1;
            }

            .detail-block {
              margin-bottom:
                2.5mm;
            }

            .field-label {
              font-size:
                8px;

              color:
                #777;

              text-transform:
                uppercase;

              letter-spacing:
                1px;

              margin-bottom:
                1mm;
            }

            .artist {
              font-size:
                16px;

              font-weight:
                700;

              color:
                #000;

              line-height:
                1.2;
            }

            .art-title {
              font-size:
                15px;

              font-style:
                italic;

              color:
                #000;

              line-height:
                1.2;
            }

            .extra-details {
              margin-top:
                2mm;

              padding-top:
                2.5mm;

              border-top:
                1px solid #eeeeee;
            }

            .extra-line {
              font-size:
                11px;

              color:
                #222;

              line-height:
                1.35;

              margin-bottom:
                1mm;
            }

            /* =========================================
               EDITABLE
            ========================================= */

            .editable {
              outline: none;

              cursor: text;

              border-radius:
                3px;
            }

            .editable:hover {
              background:
                #fffde7;
            }

            .editable:focus {
              background:
                #fff8cc;

              box-shadow:
                inset 0 0 0 1px
                #d6b656;
            }

            /* =========================================
               CUTTING LINE
            ========================================= */

            .cut-line {
              width: 100%;

              border-top:
                1px dashed #777;

              margin-top:
                0mm;
            }

            /* =========================================
               PRINT
            ========================================= */

            @media print {

              html,
              body {
                background:
                  #ffffff;
              }

              body {
                padding: 0;
              }

              .topbar {
                display:
                  none !important;
              }

              .paper {
                width:
                  210mm;

                height:
                  297mm;

                margin: 0;

                padding:
                  9mm 10mm;

                box-shadow:
                  none;

                overflow:
                  hidden;

                page-break-after:
                  always;
              }

              .paper:last-child {
                page-break-after:
                  auto;
              }

              .editable:hover,
              .editable:focus {
                background:
                  transparent;

                box-shadow:
                  none;
              }

              .sticker-item {
                page-break-inside:
                  avoid;

                break-inside:
                  avoid;
              }
            }

          </style>

        </head>

        <body>

          <!-- =====================================
               TOP BAR
          ====================================== -->

          <div class="topbar">

            <button
              class="print-button"
              onclick="window.print()"
            >
              🖨 Print
            </button>

          </div>

          <!-- =====================================
               PAGES
          ====================================== -->

          ${pagesHtml}

        </body>

      </html>
    `);

    previewWindow.document.close();
    previewWindow.focus();
  };

  // ===================================================
  // LOT COUNT
  // ===================================================

  const lotCount = lotNumberData
    .split(/[\n,\s]+/)
    .filter(
      (item) => item.trim()
    ).length;

  // ===================================================
  // RENDER
  // ===================================================

  return (
    <Box className={classes.root}>

      <Grid
        container
        spacing={3}
      >

        {/* =========================================
            LOT NUMBERS
        ========================================= */}

        <Grid
          item
          xs={12}
          md={5}
        >

          <Card
            className={classes.card}
          >

            <Box
              className={
                classes.sectionHeader
              }
            >

              <Box
                className={
                  classes.titleRow
                }
              >

                <Box
                  className={
                    classes.headerIcon
                  }
                >
                  <FormatListNumberedIcon
                    fontSize="small"
                  />
                </Box>

                <Box>

                  <Typography
                    className={
                      classes.title
                    }
                  >
                    Lot Selection
                  </Typography>

                  <Typography
                    className={
                      classes.subtitle
                    }
                  >
                    Enter the lot numbers
                    for sticker generation
                  </Typography>

                </Box>

              </Box>

            </Box>

            <Divider />

            <CardContent
              className={
                classes.content
              }
            >

              <TextField
                fullWidth
                multiline
                rows={15}
                variant="outlined"
                className={
                  classes.textArea
                }
                label="Lot Numbers"
                placeholder={
                  "Example:\n1\n2\n3\n4"
                }
                value={lotNumberData}
                onChange={(e) =>
                  setLotNumber(
                    e.target.value
                  )
                }
              />

              <Box
                className={
                  classes.infoBox
                }
              >

                <Box
                  className={
                    classes.infoRow
                  }
                >

                  <Typography
                    className={
                      classes.infoLabel
                    }
                  >
                    Total Lots
                  </Typography>

                  <Typography
                    className={
                      classes.infoValue
                    }
                  >
                    {lotCount}
                  </Typography>

                </Box>

                <Box
                  className={
                    classes.infoRow
                  }
                >

                  <Typography
                    className={
                      classes.infoLabel
                    }
                  >
                    Lots per Page
                  </Typography>

                  <Typography
                    className={
                      classes.infoValue
                    }
                  >
                    3
                  </Typography>

                </Box>

                <Box
                  className={
                    classes.infoRow
                  }
                >

                  <Typography
                    className={
                      classes.infoLabel
                    }
                  >
                    Estimated Pages
                  </Typography>

                  <Typography
                    className={
                      classes.infoValue
                    }
                  >
                    {Math.ceil(
                      lotCount / 3
                    ) || 0}
                  </Typography>

                </Box>

              </Box>

            </CardContent>

          </Card>

        </Grid>

        {/* =========================================
            AUCTION
        ========================================= */}

        <Grid
          item
          xs={12}
          md={7}
        >

          <Card
            className={classes.card}
          >

            <Box
              className={
                classes.sectionHeader
              }
            >

              <Box
                className={
                  classes.titleRow
                }
              >

                <Box
                  className={
                    classes.headerIcon
                  }
                >
                  <LocalOfferIcon
                    fontSize="small"
                  />
                </Box>

                <Box>

                  <Typography
                    className={
                      classes.title
                    }
                  >
                    Sticker Details
                  </Typography>

                  <Typography
                    className={
                      classes.subtitle
                    }
                  >
                    Select the auction or event
                    for the stickers
                  </Typography>

                </Box>

              </Box>

            </Box>

            <Divider />

            <CardContent
              className={
                classes.content
              }
            >

              <Box mb={2}>

                <Autocomplete
                  options={
                    geteventList
                  }
                  getOptionLabel={(
                    option
                  ) =>
                    option
                      ? option.auction_name ||
                        ""
                      : ""
                  }
                  value={
                    geteventList.find(
                      (item) =>
                        String(item.id) ===
                        String(eventId)
                    ) || null
                  }
                  onChange={(
                    event,
                    selectedAuction
                  ) => {

                    if (
                      selectedAuction
                    ) {

                      setEventName(
                        selectedAuction.auction_name ||
                          ""
                      );

                      setEventId(
                        selectedAuction.id
                      );

                    } else {

                      setEventName("");
                      setEventId("");

                    }

                  }}
                  renderInput={(
                    params
                  ) => (
                    <TextField
                      {...params}
                      fullWidth
                      label="Event / Auction"
                      variant="outlined"
                      className={
                        classes.field
                      }
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <EventIcon
                              fontSize="small"
                              style={{
                                marginRight: 7,
                                color:
                                  "#9ca3af",
                              }}
                            />

                            {
                              params
                                .InputProps
                                .startAdornment
                            }
                          </>
                        ),
                      }}
                    />
                  )}
                />

              </Box>

              {/* EVENT PREVIEW */}

              <Box
                className={
                  classes.infoBox
                }
              >

                <Typography
                  style={{
                    fontSize: 12,
                    color: "#6b7280",
                    marginBottom: 5,
                  }}
                >
                  Selected Event
                </Typography>

                <Typography
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color:
                      eventName
                        ? "#111827"
                        : "#9ca3af",
                  }}
                >
                  {eventName ||
                    "No event selected"}
                </Typography>

              </Box>

            </CardContent>

            <Divider />

            <Box
              className={
                classes.actionSection
              }
            >

              <Button
                variant="contained"
                color="primary"
                className={
                  classes.actionButton
                }
                startIcon={
                  <VisibilityIcon />
                }
                onClick={
                  handlePreview
                }
                disabled={
                  !eventId ||
                  !lotNumberData.trim()
                }
              >
                Preview & Print
              </Button>

            </Box>

          </Card>

        </Grid>

      </Grid>

      {/* =========================================
          LOADING
      ========================================= */}

      <Backdrop
        className={
          classes.backdrop
        }
        open={loading}
      >
        <CircularProgress
          color="inherit"
        />
      </Backdrop>

    </Box>
  );
};

export default Views;